import { useCallback, useEffect, useRef, useState } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import ThemeToggleButton from './ThemeToggleButton';
import AuthButton from './AuthButton';
import { isInternalPathActive, navigationGroups } from '../config/navigation';

const ChevronIcon = ({ open = false, className = 'h-4 w-4' }) => (
  <svg
    aria-hidden="true"
    className={`${className} transition-transform duration-200 motion-reduce:transition-none ${open ? 'rotate-180' : ''}`}
    fill="none"
    viewBox="0 0 24 24"
    stroke="currentColor"
  >
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
  </svg>
);

const ArrowIcon = () => (
  <svg aria-hidden="true" className="h-4 w-4 flex-none" fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" />
  </svg>
);

const Header = ({ currentTheme, onToggleTheme }) => {
  const [openDesktopGroup, setOpenDesktopGroup] = useState(null);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [openMobileGroup, setOpenMobileGroup] = useState('site-health');
  const headerRef = useRef(null);
  const desktopTriggerRefs = useRef({});
  const mobileMenuButtonRef = useRef(null);
  const location = useLocation();

  const closeDesktopMenu = useCallback((returnFocus = false) => {
    const groupId = openDesktopGroup;
    setOpenDesktopGroup(null);
    if (returnFocus && groupId) {
      window.requestAnimationFrame(() => desktopTriggerRefs.current[groupId]?.focus());
    }
  }, [openDesktopGroup]);

  const closeMobileMenu = useCallback((returnFocus = false) => {
    setIsMobileMenuOpen(false);
    setOpenMobileGroup('site-health');
    if (returnFocus) {
      window.requestAnimationFrame(() => mobileMenuButtonRef.current?.focus());
    }
  }, []);

  useEffect(() => {
    setOpenDesktopGroup(null);
    setIsMobileMenuOpen(false);
    setOpenMobileGroup('site-health');
  }, [location.pathname]);

  useEffect(() => {
    const handlePointerDown = (event) => {
      if (!headerRef.current?.contains(event.target)) {
        setOpenDesktopGroup(null);
        setIsMobileMenuOpen(false);
      }
    };

    const handleKeyDown = (event) => {
      if (event.key !== 'Escape') return;

      if (isMobileMenuOpen) {
        event.preventDefault();
        closeMobileMenu(true);
        return;
      }

      if (openDesktopGroup) {
        event.preventDefault();
        closeDesktopMenu(true);
      }
    };

    document.addEventListener('pointerdown', handlePointerDown);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('pointerdown', handlePointerDown);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [closeDesktopMenu, closeMobileMenu, isMobileMenuOpen, openDesktopGroup]);

  const handleDesktopBlur = (event, groupId) => {
    if (!event.currentTarget.contains(event.relatedTarget) && openDesktopGroup === groupId) {
      setOpenDesktopGroup(null);
    }
  };

  const renderDesktopGroup = (group) => {
    const isOpen = openDesktopGroup === group.id;
    const isActive = group.links.some((item) => isInternalPathActive(location.pathname, item.to));
    const panelId = `desktop-${group.id}-panel`;

    return (
      <div
        key={group.id}
        className="relative"
        onBlur={(event) => handleDesktopBlur(event, group.id)}
      >
        <button
          ref={(element) => {
            desktopTriggerRefs.current[group.id] = element;
          }}
          type="button"
          className={`ui-nav-trigger ${isActive ? 'ui-nav-trigger-active' : ''}`}
          aria-expanded={isOpen}
          aria-controls={panelId}
          onClick={() => setOpenDesktopGroup((current) => current === group.id ? null : group.id)}
        >
          {group.label}
          <ChevronIcon open={isOpen} />
        </button>

        <div
          id={panelId}
          hidden={!isOpen}
          className="absolute left-0 top-full mt-2 w-[320px] overflow-hidden rounded-2xl border border-gray-200 bg-white p-2 shadow-2xl ring-1 ring-navy-100/70 dark:border-gray-700 dark:bg-gray-900 dark:ring-navy-800/50"
        >
            <div className="px-3 pb-2 pt-2">
              <p className="text-xs font-semibold uppercase tracking-[0.16em] text-gray-600 dark:text-gray-300">
                {group.label}
              </p>
              <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">{group.description}</p>
            </div>

            <div className="space-y-1">
              {group.links.filter((item) => item.primary !== false).map((item) => {
                const isItemActive = isInternalPathActive(location.pathname, item.to);
                return (
                  <NavLink
                    key={item.to}
                    to={item.to}
                    aria-current={isItemActive ? 'page' : undefined}
                    className={`group flex items-center justify-between gap-3 rounded-xl px-3 py-2.5 transition-colors duration-150 motion-reduce:transition-none ${
                      isItemActive
                        ? 'bg-emerald-50 text-emerald-900 dark:bg-emerald-900/25 dark:text-emerald-100'
                        : 'text-gray-900 hover:bg-gray-100 dark:text-white dark:hover:bg-gray-800'
                    }`}
                    onClick={() => setOpenDesktopGroup(null)}
                  >
                    <span className="min-w-0">
                      <span className="block text-sm font-semibold">{item.title}</span>
                      <span className="mt-0.5 block text-sm leading-5 text-gray-600 dark:text-gray-300">
                        {item.description}
                      </span>
                    </span>
                    <span className="text-navy-600 transition-transform duration-150 group-hover:translate-x-0.5 motion-reduce:transition-none dark:text-navy-300">
                      <ArrowIcon />
                    </span>
                  </NavLink>
                );
              })}
            </div>
        </div>
      </div>
    );
  };

  return (
    <header
      ref={headerRef}
      className="sticky top-0 z-50 border-b border-gray-200/80 bg-white/95 shadow-sm backdrop-blur-xl dark:border-gray-800 dark:bg-gray-900/95"
    >
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="flex min-h-[68px] items-center justify-between gap-4">
          <div className="flex min-w-0 items-center gap-7">
            <NavLink to="/" className="group flex flex-none items-center gap-2" aria-label="DomainOptic homepage">
              <img
                src={currentTheme === 'dark' ? '/icon-only-white.png' : '/icon-only-navy.png'}
                alt=""
                aria-hidden="true"
                width="36"
                height="36"
                decoding="async"
                className="h-9 w-9 object-contain transition-transform duration-200 group-hover:scale-105 motion-reduce:transition-none"
              />
              <span className="text-xl font-bold tracking-tight text-gray-950 dark:text-white">DomainOptic</span>
            </NavLink>

            <nav aria-label="Primary navigation" className="hidden items-center gap-1 lg:flex">
              {navigationGroups.map(renderDesktopGroup)}
            </nav>
          </div>

          <div className="flex flex-none items-center gap-2 sm:gap-3">
            <NavLink
              to="/search-visibility/"
              className="ui-button-primary hidden lg:inline-flex"
            >
              Check visibility
            </NavLink>
            <NavLink
              to="/audit/"
              className="hidden min-h-11 items-center gap-2 rounded-lg border-2 border-emerald-700 px-4 py-2 text-sm font-semibold text-emerald-800 transition-colors hover:bg-emerald-50 motion-reduce:transition-none dark:border-emerald-400/60 dark:text-emerald-300 dark:hover:bg-emerald-900/20 lg:inline-flex"
            >
              Run audit
            </NavLink>
            <div className="hidden xl:block">
              <AuthButton />
            </div>
            <ThemeToggleButton currentTheme={currentTheme} onToggleTheme={onToggleTheme} />

            <button
              ref={mobileMenuButtonRef}
              type="button"
              className="ui-icon-button lg:hidden"
              aria-label={isMobileMenuOpen ? 'Close navigation' : 'Open navigation'}
              aria-controls="mobile-navigation-panel"
              aria-expanded={isMobileMenuOpen}
              onClick={() => {
                setIsMobileMenuOpen((open) => !open);
                setOpenDesktopGroup(null);
              }}
            >
              {isMobileMenuOpen ? (
                <svg aria-hidden="true" className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                </svg>
              ) : (
                <svg aria-hidden="true" className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 6h16M4 12h16M4 18h16" />
                </svg>
              )}
            </button>
          </div>
        </div>
      </div>

      <div
        id="mobile-navigation-panel"
        hidden={!isMobileMenuOpen}
        className="border-t border-gray-200 bg-white dark:border-gray-800 dark:bg-gray-900 lg:hidden"
      >
          <nav aria-label="Mobile navigation" className="mx-auto max-w-7xl space-y-3 px-4 py-4 sm:px-6">
            <NavLink to="/search-visibility/" className="ui-button-primary flex w-full justify-center">
              Check AI visibility
            </NavLink>
            <NavLink
              to="/audit/"
              className="flex min-h-11 w-full items-center justify-center gap-2 rounded-lg border-2 border-emerald-700 px-4 py-2 text-sm font-semibold text-emerald-800 transition-colors hover:bg-emerald-50 motion-reduce:transition-none dark:border-emerald-400/60 dark:text-emerald-300 dark:hover:bg-emerald-900/20"
            >
              Run security audit
            </NavLink>

            {navigationGroups.map((group) => {
              const isOpen = openMobileGroup === group.id;
              const isActive = group.links.some((item) => isInternalPathActive(location.pathname, item.to));
              const panelId = `mobile-${group.id}-panel`;

              return (
                <div key={group.id} className="overflow-hidden rounded-xl border border-gray-200 dark:border-gray-700">
                  <button
                    type="button"
                    className={`flex min-h-12 w-full items-center justify-between gap-3 px-4 py-3 text-left text-sm font-semibold ${
                      isActive
                        ? 'bg-emerald-50 text-emerald-900 dark:bg-emerald-900/25 dark:text-emerald-100'
                        : 'bg-gray-50 text-gray-950 dark:bg-gray-800 dark:text-white'
                    }`}
                    aria-expanded={isOpen}
                    aria-controls={panelId}
                    onClick={() => setOpenMobileGroup((current) => current === group.id ? null : group.id)}
                  >
                    <span>
                      <span className="block">{group.label}</span>
                      <span className="mt-0.5 block text-sm font-normal text-gray-600 dark:text-gray-300">
                        {group.description}
                      </span>
                    </span>
                    <ChevronIcon open={isOpen} />
                  </button>

                  <div id={panelId} hidden={!isOpen} className="divide-y divide-gray-200 dark:divide-gray-800">
                      {group.links.filter((item) => item.primary !== false).map((item) => {
                        const isItemActive = isInternalPathActive(location.pathname, item.to);
                        return (
                          <NavLink
                            key={item.to}
                            to={item.to}
                            aria-current={isItemActive ? 'page' : undefined}
                            className={`block min-h-12 px-4 py-3 ${
                              isItemActive
                                ? 'bg-navy-50 text-navy-900 dark:bg-navy-900/30 dark:text-navy-100'
                                : 'text-gray-800 hover:bg-gray-50 dark:text-gray-200 dark:hover:bg-gray-800'
                            }`}
                          >
                            <span className="block text-sm font-semibold">{item.title}</span>
                            <span className="mt-0.5 block text-sm leading-5 text-gray-600 dark:text-gray-300">
                              {item.description}
                            </span>
                          </NavLink>
                        );
                      })}
                  </div>
                </div>
              );
            })}

            <div className="border-t border-gray-200 pt-3 dark:border-gray-800 xl:hidden">
              <AuthButton variant="mobile" />
            </div>
          </nav>
      </div>
    </header>
  );
};

export default Header;
